import React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, { FilterType, Responsive, SelectableRows } from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../../theme";
import {
    AlertColor,
    Box,
    Modal,
    Typography,
    Button,
    CircularProgress,
    Collapse,
    TableRow,
    TableCell,
    Tooltip
} from "@mui/material";

import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import LiveTvIcon from "@mui/icons-material/LiveTv";

import "./index.css";
import config, { ApiSystemData } from "../../config";
import { SyntheticEvent, useState, useEffect } from "react";
import ButtonWithDialog from "../DialogButton";
import CustomSnackbar from "../../components/CustomSnackbar";
import { useAuth } from "../../context/AuthContext";
import { SystemWithRegion, useSystemsQuery } from "../../features/systems/queries";
import {
    useDeleteSystemMutation,
    useDownloadCertMutation,
    useRegenerateCertMutation
} from "../../features/systems/mutations";
import { triggerCertificateDownload } from "../../features/systems/utils";
import SystemLogsTab from "./SystemLogsTab";
import SystemDetail from "./SystemDetail";
import SystemLiveViewSessions from "./SystemLiveViewSessions";

const { ID_CREATE_NEW } = config.app;

export default function SystemsList() {
    const { headersWithAuth } = useAuth();
    const { data: systems = [], isLoading, isError, refetch } = useSystemsQuery(headersWithAuth);
    const deleteSystem = useDeleteSystemMutation(headersWithAuth);
    const downloadCert = useDownloadCertMutation(headersWithAuth);
    const regenerateCert = useRegenerateCertMutation(headersWithAuth);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSystemId, setSelectedSystemId] = useState<string | null>(null);
    const [selectedSystemRegion, setSelectedSystemRegion] = useState<string | null>(null);

    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

    const [expandedLogsRowId, setExpandedLogsRowId] = useState<string | null>(null);
    const [expandedLiveViewRowId, setExpandedLiveViewRowId] = useState<string | null>(null);

    const toggleLiveViewExpand = (systemId: string) => {
        if (expandedLiveViewRowId === systemId) {
            setExpandedLiveViewRowId(null);
        } else {
            setExpandedLiveViewRowId(systemId);
            setExpandedRowId(null);
            setExpandedLogsRowId(null);
        }
    };

    useEffect(() => {
        if (isError) {
            setSnackbarMessage("Could not load all systems data. Some regions may be unavailable.");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
        }
    }, [isError]);

    const toggleLogsExpand = (systemId: string) => {
        if (expandedLogsRowId === systemId) {
            setExpandedLogsRowId(null);
        } else {
            setExpandedLogsRowId(systemId);
            setExpandedRowId(null);
        }
    };

    const toggleExpand = (systemId: string) => {
        if (expandedRowId === systemId) {
            setExpandedRowId(null);
        } else {
            setExpandedRowId(systemId);
            setExpandedLogsRowId(null);
        }
    };

    const openModal = (systemId: string, region: string) => {
        setSelectedSystemId(systemId);
        setSelectedSystemRegion(region);
        setModalOpen(true);
    };

    const closeModal = () => {
        setSelectedSystemId(null);
        setSelectedSystemRegion(null);
        setModalOpen(false);
    };

    function formatDateTime(datetime: string): string {
        if (!datetime) return "-";
        const date = new Date(datetime);
        return date.toLocaleString();
    }

    const dataWithDefaultLastLogin = (systems || []).map((system: SystemWithRegion) => ({
        ...system,
        updateTs: formatDateTime(system.updateTs),
        region: system.region
    }));

    const handleSnackbarClose = (event: SyntheticEvent<any, Event> | Event, reason?: string) => {
        if (reason === "clickaway") return;
        setOpenSnackbar(false);
    };

    const handleDelete = async (id: string, region: string) => {
        deleteSystem.mutate(
            { id, region },
            {
                onSuccess: () => {
                    setSnackbarMessage("System deleted successfully!");
                    setSnackbarSeverity("success");
                    setOpenSnackbar(true);
                    refetch();
                },
                onError: (error: any) => {
                    setSnackbarMessage(`Failed to delete system: ${error.message || "unknown error"}`);
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                }
            }
        );
    };

    const handleDownload = async (systemId: string, region: string) => {
        downloadCert.mutate(
            { id: systemId, region },
            {
                onSuccess: (response) => {
                    const { url } = response[0];
                    triggerCertificateDownload(url, `${systemId}_certs.zip`);
                    setSnackbarMessage(`System certificate download complete for the id: ${systemId}`);
                    setSnackbarSeverity("success");
                    setOpenSnackbar(true);
                },
                onError: (error: any) => {
                    setSnackbarMessage(`Download failed for systemId: ${systemId}`);
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                }
            }
        );
    };

    const handleRegenerateCert = (id: string, region: string) => {
        regenerateCert.mutate(
            { id, region },
            {
                onSuccess: () => {
                    setSnackbarMessage(`Certificate successfully regenerated for system ID: ${id}`);
                    setSnackbarSeverity("success");
                    setOpenSnackbar(true);
                    closeModal();
                },
                onError: (error: any) => {
                    setSnackbarMessage(`Error regenerating certificate: ${error.message}`);
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                    closeModal();
                }
            }
        );
    };

    const allSystems = systems || [];

    const customRowRender = (data: any[], dataIndex: number) => {
        const system = dataWithDefaultLastLogin[dataIndex];
        if (!system) return null;

        const isDetailsExpanded = expandedRowId === system.id;
        const isLogsExpanded = expandedLogsRowId === system.id;
        const isLiveViewExpanded = expandedLiveViewRowId === system.id;
        const region = system.region;

        return (
            <React.Fragment key={`row-${system.id}`}>
                <TableRow hover>
                    {data.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                </TableRow>

                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 0 }} colSpan={12}>
                        <Collapse in={isDetailsExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, marginBottom: 4 }}>
                                <SystemDetail systemId={system.id} region={region} />
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 0 }} colSpan={12}>
                        <Collapse in={isLogsExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, marginBottom: 4 }}>
                                <Typography variant="h6" gutterBottom component="div">
                                    System Logs
                                </Typography>
                                <SystemLogsTab systemId={system.id} region={region} />
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 0 }} colSpan={12}>
                        <Collapse in={isLiveViewExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, marginBottom: 4 }}>
                                <SystemLiveViewSessions systemId={system.id} region={region} />
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    };

    const deleteButton = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (SystemId: string, tableMeta: { rowData: any[] }) => {
            const systemToDelete = allSystems.find((system: ApiSystemData) => system.id === SystemId);
            if (!systemToDelete) return null;
            const region = tableMeta.rowData[8];
            return (
                <ButtonWithDialog onDelete={() => handleDelete(systemToDelete.id, region)} typeOfOperation="Delete" />
            );
        }
    };

    const downloadButton = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (systemId: string, tableMeta: { rowData: any[] }) => {
            const system = allSystems.find((sys) => sys.id === systemId);
            const isProvisioned = system?.status === "provisioned";
            const region = tableMeta.rowData[8];

            return (
                <Tooltip title={isProvisioned ? "Download Certificate" : "System not provisioned"}>
                    <span>
                        <IconButton
                            onClick={() => handleDownload(systemId, region)}
                            color="primary"
                            disabled={!isProvisioned}
                            size="small"
                        >
                            <DownloadIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            );
        }
    };

    const refreshButton = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (systemId: string, tableMeta: { rowData: any[] }) => {
            const region = tableMeta.rowData[8];
            return (
                <Tooltip title="Regenerate Certificate">
                    <IconButton onClick={() => openModal(systemId, region)} color="primary" size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const buttonViewOptions = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (value: any) => (
            <Tooltip title="Edit">
                <IconButton onClick={() => navigate(`/system/${value}`)} color="primary" size="small">
                    <EditRoundedIcon />
                </IconButton>
            </Tooltip>
        )
    };

    const logsButton = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (systemId: string, tableMeta: { rowData: any[] }) => {
            const isExpanded = expandedLogsRowId === systemId;
            return (
                <Tooltip title={isExpanded ? "Hide logs" : "View system logs"}>
                    <IconButton
                        aria-label={isExpanded ? "collapse logs" : "expand logs"}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleLogsExpand(systemId);
                        }}
                        color="primary"
                        data-cy={`logs-toggle-${systemId}`}
                    >
                        <DescriptionIcon />
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const liveViewButton = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (systemId: string, tableMeta: { rowData: any[] }) => {
            const isExpanded = expandedLiveViewRowId === systemId;
            const region = tableMeta.rowData[8]; // Get region from the row data

            return (
                <Tooltip title={isExpanded ? "Hide Live View sessions" : "View Live View sessions"}>
                    <IconButton
                        aria-label={isExpanded ? "collapse live view" : "expand live view"}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleLiveViewExpand(systemId);
                        }}
                        color="primary"
                        data-cy={`liveview-toggle-${systemId}`}
                    >
                        <LiveTvIcon />
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const newSystemTitle = (
        <Button
            data-cy="create-system-button"
            onClick={() => navigate(`/system/${ID_CREATE_NEW}`)}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
        >
            Create System
        </Button>
    );

    const expandButton = {
        sort: false,
        filter: false,
        viewColumns: true,
        customBodyRender: (value: string, tableMeta: { rowData: any[] }) => {
            const systemId = value;
            const isExpanded = expandedRowId === systemId;

            return (
                <Tooltip title={isExpanded ? "Hide details" : "Show details"}>
                    <IconButton
                        aria-label={isExpanded ? "collapse row" : "expand row"}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(systemId);
                        }}
                        data-cy={`expand-toggle-${systemId}`}
                    >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const COLUMNS = [
        { label: " ", name: "id", options: expandButton },
        { label: " ", name: "id", options: buttonViewOptions },
        { label: " ", name: "id", options: liveViewButton },
        { label: " ", name: "id", options: downloadButton },
        { label: " ", name: "id", options: deleteButton },
        { label: " ", name: "id", options: refreshButton },
        { label: " ", name: "id", options: logsButton },
        { label: "ID", name: "id", options: {} },
        { label: "Name", name: "name", options: {} },
        { label: "Region", name: "region", options: {} },
        { label: "Company", name: "companyName", options: {} },
        { label: "System Update Time", name: "updateTs", options: {} }
    ];

    const OPTIONS = {
        download: false,
        filter: true,
        filterType: "dropdown" as FilterType,
        pagination: false,
        print: false,
        responsive: "vertical" as Responsive,
        search: true,
        selectableRows: "none" as SelectableRows,
        selectableRowsHideCheckboxes: true,
        storageKey: "systems",
        textLabels: {
            body: {
                noMatch: isLoading ? "Loading ..." : isError ? "Failed to load systems." : "No systems available."
            }
        },
        customRowRender: customRowRender
    };

    const getMuiTheme = () => {
        const baseTheme = theme;
        return {
            ...baseTheme,
            components: {
                ...baseTheme.components,
                MUIDataTableHeadCell: {
                    styleOverrides: {
                        contentWrapper: {
                            justifyContent: "left"
                        }
                    }
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            padding: "16px 8px"
                        }
                    }
                }
            }
        };
    };

    return (
        <Box className="systems-table">
            <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                    title={newSystemTitle}
                    data={dataWithDefaultLastLogin}
                    columns={COLUMNS}
                    options={OPTIONS}
                />
            </ThemeProvider>

            <CustomSnackbar
                open={openSnackbar}
                handleClose={handleSnackbarClose}
                message={snackbarMessage}
                severity={snackbarSeverity}
                autoHideDuration={30000}
            />

            <Modal open={modalOpen} onClose={closeModal}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2
                    }}
                >
                    <Typography variant="h6">Regenerate Certificate</Typography>
                    <Typography sx={{ mt: 2 }}>
                        Are you sure you want to regenerate the certificate for system ID:{" "}
                        <strong>{selectedSystemId}</strong> in region <strong>{selectedSystemRegion}</strong>?
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                        <Button onClick={closeModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleRegenerateCert(selectedSystemId || "", selectedSystemRegion || "")}
                            variant="contained"
                            color="primary"
                            disabled={regenerateCert.isPending}
                            startIcon={regenerateCert.isPending ? <CircularProgress size={20} /> : null}
                        >
                            Continue
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
